"use client";

/**
 * Popover that renders INLINE (absolute under its trigger) on the /m shell.
 *
 * ⚠️ Radix popper anchors drift vertically in the WKWebView — filter
 * dropdowns opened ~60px below their trigger, burying themselves under the
 * next field (first seen on the carrier pickers, then the lead-assignment
 * filters, then Resources). On the shell the panel is a plain absolute child
 * of the trigger's wrapper, which cannot drift; desktop keeps the real Radix
 * Popover. Supports controlled (open/onOpenChange) and uncontrolled use,
 * mirroring the Radix API shape.
 */
import { useState, cloneElement } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";

export function FilterPop({
  trigger,
  children,
  className,
  align = "start",
  open: openProp,
  onOpenChange,
  modal,
}: {
  trigger: React.ReactElement;
  children: React.ReactNode;
  className?: string;
  align?: "start" | "end";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Forwarded to the desktop Radix Popover (needed inside Dialogs). */
  modal?: boolean;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const setOpen = (o: boolean) => {
    if (!isControlled) setUncontrolledOpen(o);
    onOpenChange?.(o);
  };

  const isMobileShell =
    typeof window !== "undefined" && window.location.pathname.startsWith("/m");

  if (isMobileShell) {
    return (
      <div className="relative">
        {cloneElement(trigger as React.ReactElement<any>, {
          onClick: (e: any) => {
            (trigger.props as any)?.onClick?.(e);
            setOpen(!open);
          },
        })}
        {open && (
          <>
            {/* invisible scrim — outside tap closes without dimming */}
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div
              className={`absolute left-0 right-0 top-[calc(100%+4px)] z-50 overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-lg ${className ?? ""}`}
            >
              {children}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align={align} className={className}>
        {children}
      </PopoverContent>
    </Popover>
  );
}
